// One dependency between two legs (groups) of ONE plan — the row shape of
// `plan_group_edges` (`@developerz/db` `schema/plans.ts`), plan 101-multi-repo-goal-legs D2.
//
// An edge says WHEN its upstream releases the dependent (`release`) and WHAT the
// dependent waits for (`hold`):
//   - `release: merged`   — the upstream's PR merged (today's `planGroupSatisfiedSql`).
//   - `release: deployed` — merged AND the merge commit's newest verdict in `environment`
//                           is `success` (D3). `environment` is non-null exactly here.
//   - `hold: start`       — the dependent is not CLAIMED until the edge releases (today).
//   - `hold: merge`       — the dependent runs now; its MERGE waits for the release (D4).
//
// Stored vocabulary: these members are the `plan_group_edges_*_ck` CHECK lists. Adding one
// is a two-release change (`docs/standards/stored-closed-sets.md`).
//
// SLICE 05 (PR 3): every reader reads `plan_group_edges`; triage states each edge as an
// object (`TriageEdgeSchema`, `@developerz/agent`) and refuses any kind but `merged` /
// `start` until slices 06/07 ship the `deployed` and `merge` gates. So every edge written
// is still `merged` / `start`, mapped from the `string[]` every plan-group writer takes by
// `legacyPlanEdges`, ONE mapping — see its docblock for why it outlived slice 05.

import { z } from 'zod';

export const PlanEdgeRelease = { Merged: 'merged', Deployed: 'deployed' } as const;
export const PlanEdgeReleaseSchema = z.enum(PlanEdgeRelease);
export type PlanEdgeRelease = z.infer<typeof PlanEdgeReleaseSchema>;

export const PlanEdgeHold = { Start: 'start', Merge: 'merge' } as const;
export const PlanEdgeHoldSchema = z.enum(PlanEdgeHold);
export type PlanEdgeHold = z.infer<typeof PlanEdgeHoldSchema>;

/** One edge into a group: `upstream` is a sibling group id of the same plan. */
export const PlanEdgeSchema = z
  .object({
    upstream: z.string().min(1),
    release: PlanEdgeReleaseSchema,
    environment: z.string().min(1).nullable(),
    hold: PlanEdgeHoldSchema,
  })
  // The `plan_group_edges_environment_ck` CHECK, stated in TS: `deployed` names the
  // environment it waits on, `merged` names none.
  .refine((e) => (e.release === PlanEdgeRelease.Deployed) === (e.environment !== null), {
    path: ['environment'],
    message: 'an edge names an environment exactly when release is deployed',
  });
export type PlanEdge = z.infer<typeof PlanEdgeSchema>;

/**
 * A `string[]` of upstream group ids as edges: each one `merged` / `start`, which is exactly
 * the gate `plan_groups.depends_on` meant. A repeated id collapses to one edge — the table's
 * key is `(plan_id, group_id, upstream_group_id)`.
 *
 * The ONE place this mapping lives. Slice 05 planned to delete it once the triage and scout
 * wires carried objects; it stays, because those are not the only producers. The plan-file
 * lane, `plan.execute`'s revise, regroup, unlink, the onboarding / scaffold one-group plans
 * and the scout `plan_authored` wire (a BOX-written wire: its object form ships reader-first,
 * after this release) all speak `string[]` and have no edge vocabulary — for them a sibling
 * id has only ever meant `merged` / `start`. Triage states the object, and its refusal
 * (`edge_kind_not_yet_gated`) guarantees nothing else reaches this mapping. The first slice
 * that admits another kind (06, `deployed`) changes the writer's input to `PlanEdge[]`.
 */
export function legacyPlanEdges(dependsOn: readonly string[]): PlanEdge[] {
  return [...new Set(dependsOn)].map((upstream) => ({
    upstream,
    release: PlanEdgeRelease.Merged,
    environment: null,
    hold: PlanEdgeHold.Start,
  }));
}
